import FormTextField from '../../../../../../ui/forms/fields/text-field';
import React, { ChangeEvent, ClipboardEvent, KeyboardEvent, FocusEvent } from 'react';
import { shippingAddressActions, rootActions, useAppDispatch, useAppSelector } from '../../../data/store';
import { removeInvisibleCharsOnPaste, updateControl, validateControl } from '../../../../../../domain/validation/validation';
import { IFormControl } from '../../../../../../ui/forms/interfaces';
import CountryAndStateFields from '../../../../../../ui/forms/fields/country-and-state-fields';
import FormPhoneField from '../../../../../../ui/forms/fields/phone/phone-field';
import { getTax } from '../../../data/data-service';
import { getCartCountries, getLocalData, getTaxAddressData } from '../../../domain/shopping-cart-address-provider';

const ShippingForm = () => {

    const dispatch = useAppDispatch();
    const cartData = useAppSelector(store => store.root.cartData);
    const countries = useAppSelector(store => store.root.countries);

    // form controls
    const shipToControl = useAppSelector(store => store.shippingAddress.shipToControl);
    const shippingAddressLine1Control = useAppSelector(store => store.shippingAddress.addressLine1Control);
    const shippingAddressLine2Control = useAppSelector(store => store.shippingAddress.addressLine2Control);
    const cityControl = useAppSelector(store => store.shippingAddress.cityControl);
    const zipCodeControl = useAppSelector(store => store.shippingAddress.zipCodeControl);
    const countryIdControl = useAppSelector(store => store.shippingAddress.countryIdControl);
    const stateIdControl = useAppSelector(store => store.shippingAddress.stateIdControl);
    const phoneControl = useAppSelector(store => store.shippingAddress.phoneControl);
    const phoneCountryIdControl = useAppSelector(store => store.shippingAddress.phoneCountryIdControl);

    const upsShippingCodeControl = useAppSelector(store => store.shippingAddress.upsShippingCodeControl);

    /**
     * On input change
     */
    const handleChangeEvent = (evt: ChangeEvent, control: IFormControl, controlName: string) => {
        const $target = evt.target as HTMLInputElement;

        dispatch(
            shippingAddressActions.setControl({
                controlName,
                control: updateControl(control, $target.value, !control.isValid)
            })
        );
    };

    /**
     * on country, state, or zipcode change ---> tax can be changed,
     * in such case ----> update the cart data
     */
    const onTaxChange = async (evt: ChangeEvent, control: IFormControl, controlName: string) => {

        const $target = evt.target as HTMLInputElement;
        let taxAddressData = null;

        switch(controlName){
            case 'countryIdControl':{
                const countryId = Number($target.value) || 0;
                taxAddressData = getTaxAddressData(countries, countryId, stateIdControl.value, zipCodeControl.value);
                break;
            }
            case 'stateIdControl':{
                const stateId = Number($target.value) || 0;
                taxAddressData = getTaxAddressData(countries, countryIdControl.value, stateId, zipCodeControl.value);
                break;
            }
            case 'zipCodeControl':{
                const zipCode = $target.value;
                taxAddressData = getTaxAddressData(countries, countryIdControl.value, stateIdControl.value, zipCode);
                break;
            }
        }

        if(!taxAddressData) return;

        const localData = getLocalData();
        localData.taxCountry2LetterCode = taxAddressData.taxCountry2LetterCode;
        localData.taxState2LetterCode = taxAddressData.taxState2LetterCode;
        localData.taxZipCode = taxAddressData.taxZipCode;

        const response = await getTax(localData);

        if(!response || response.statusCode !== 200 || !response.data.isValid || !response.data.relatedData) return;

        dispatch(
            rootActions.setCartData(response.data.relatedData)
        );
    }

    /**
     * handle key up event
     */
    const handleKeyUp = (evt: KeyboardEvent, control: IFormControl, controlName: string) => {
        if(evt.key === 'Escape') {
            dispatch(
                shippingAddressActions.setControl({
                    controlName,
                    control: updateControl(control, '', !control.isValid)
                })
            );
        }
    };

    /**
     * validate on blur
     */
    const handleBlurEvent = (evt: FocusEvent, control: IFormControl, controlName: string) => {
        dispatch(
            shippingAddressActions.setControl({
                controlName,
                control: validateControl(control),
            })
        );
    };

    /**
     * remove invisible characters on paste event
     */
    const handlePasteEvent = (evt: ClipboardEvent<HTMLInputElement>, control: IFormControl, controlName: string) => {

        dispatch(
            shippingAddressActions.setControl({
                controlName,
                control: updateControl(control,  removeInvisibleCharsOnPaste(evt), !control.isValid)
            })
        );
    };

    /**
     * on country / state changes --> shipping options should be reset
     */
    const resetShipping = () => {
        dispatch(
            shippingAddressActions.main({
                upsShippingOptions: [],
                upsShippingError: '',
            })
        );

        dispatch(
            shippingAddressActions.setControl({
                controlName: 'upsShippingCodeControl',
                control: updateControl(upsShippingCodeControl, '', true)
            })
        );
    }

    return (
        <>
            <FormTextField
                id={ 'shipTo' }
                label={ 'Ship To Name' }
                control={ shipToControl }
                maxLength={ 200 }
                qa={ 'cart-step3-ship-name-tb' }
                textBoxClasses="rounded"
                onChange={ evt => {
                    handleChangeEvent(evt, shipToControl, 'shipToControl');
                }}
                onBlur={ evt => {
                    handleBlurEvent(evt, shipToControl, 'shipToControl');
                }}
                onKeyUp={ evt => {
                    handleKeyUp(evt, shipToControl, 'shipToControl');
                }}
                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                    handlePasteEvent(evt, shipToControl, 'shipToControl');
                }}
            />

            <FormTextField
                id={ 'shippingAddressLine1' }
                label={ 'Address Line 1' }
                qa={ 'cart-step3-ship-addr1-tb' }
                control={ shippingAddressLine1Control }
                maxLength={ 100 }
                textBoxClasses="rounded"
                onChange={ evt => {
                    handleChangeEvent(evt, shippingAddressLine1Control, 'addressLine1Control');
                }}
                onBlur={ evt => {
                    handleBlurEvent(evt, shippingAddressLine1Control, 'addressLine1Control');
                }}
                onKeyUp={ evt => {
                    handleKeyUp(evt, shippingAddressLine1Control, 'addressLine1Control');
                }}
                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                    handlePasteEvent(evt, shippingAddressLine1Control, 'addressLine1Control');
                }}
            />

            <FormTextField
                id={ 'shippingAddressLine2' }
                label={ 'Address Line 2 (Optional)' }
                control={ shippingAddressLine2Control }
                maxLength={ 100 }
                textBoxClasses="rounded"
                onChange={ evt => {
                    handleChangeEvent(evt, shippingAddressLine2Control, 'addressLine2Control');
                }}
                onBlur={ evt => {
                    handleBlurEvent(evt, shippingAddressLine2Control, 'addressLine2Control');
                }}
                onKeyUp={ evt => {
                    handleKeyUp(evt, shippingAddressLine2Control, 'addressLine2Control');
                }}
                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                    handlePasteEvent(evt, shippingAddressLine2Control, 'addressLine2Control');
                }}
            />

            <FormTextField
                id={ 'shippingCity' }
                label={ 'City' }
                control={ cityControl }
                maxLength={ 100 }
                qa={ 'cart-step3-ship-city-tb' }
                textBoxClasses="rounded"
                onChange={ evt => {
                    handleChangeEvent(evt, cityControl, 'cityControl');
                    resetShipping();
                }}
                onBlur={ evt => {
                    handleBlurEvent(evt, cityControl, 'cityControl');
                }}
                onKeyUp={ evt => {
                    handleKeyUp(evt, cityControl, 'cityControl');
                }}
                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                    handlePasteEvent(evt, cityControl, 'cityControl');
                }}
            />

            <CountryAndStateFields
                idPrefix="shipping"
                countries={ getCartCountries(countries, cartData) }
                countryIdControl={ countryIdControl }
                stateIdControl={ stateIdControl }
                qaCountry={ 'cart-step3-ship-ctry-drop' }
                qaState={ 'cart-step3-ship-state-drop' }
                onBlur={ () => {} }
                onCountryChange={ async evt => {
                    handleChangeEvent(evt, countryIdControl, 'countryIdControl');
                    resetShipping();
                    await onTaxChange(evt, countryIdControl, 'countryIdControl');
                }}
                onStateChange={ async evt => {
                    const $target = evt.target as HTMLInputElement;
                    let control = updateControl(stateIdControl, $target.value, !stateIdControl.isValid);
                    control = validateControl(control);

                    dispatch(
                        shippingAddressActions.setControl({
                            controlName: 'stateIdControl',
                            control,
                        })
                    );

                    resetShipping();

                    await onTaxChange(evt, control, 'stateIdControl');
                }}
                onClick={ () => {} }
            />

            <FormTextField
                id={ 'shippingZipCode' }
                label={ 'Zip Code' }
                control={ zipCodeControl }
                qa={ 'cart-step3-ship-zip-tb' }
                maxLength={ 10 }
                textBoxClasses="rounded"
                onChange={ evt => {
                    handleChangeEvent(evt, zipCodeControl, 'zipCodeControl');
                    resetShipping();
                }}
                onBlur={ async evt => {
                    handleBlurEvent(evt, zipCodeControl, 'zipCodeControl');
                    await onTaxChange(evt, zipCodeControl, 'zipCodeControl');
                }}
                onKeyUp={ evt => {
                    handleKeyUp(evt, zipCodeControl, 'zipCodeControl');
                }}
                onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                    handlePasteEvent(evt, zipCodeControl, 'zipCodeControl');
                }}
            />

            <FormPhoneField
                id={ 'shippingPhone' }
                label={ 'Phone (Digits only)' }
                countries={ countries }
                phoneControl={ phoneControl }
                phoneCountryIdControl={ phoneCountryIdControl }
                textBoxClasses="rounded"
                qa={ 'cart-step3-ship-phone-tb' }
                onPhoneChange={ evt => {
                    handleChangeEvent(evt, phoneControl, 'phoneControl');
                }}
                onCountryIdChange={ (countryId: number) => {
                    dispatch(
                        shippingAddressActions.setControl({
                            controlName: 'phoneCountryIdControl',
                            control: updateControl(phoneCountryIdControl, countryId, !phoneCountryIdControl.isValid)
                        })
                    );
                }}
            />
        </>
    )
};

export default ShippingForm;